/**
 * Google Analytics 4 (gtag.js) — when VITE_GA4_MEASUREMENT_ID is set at build time, Vite injects
 * the gtag/js script + Consent Mode defaults (denied) into index.html so Google's verification
 * and crawlers see the tag. This module completes setup after the user accepts Analytics
 * (sitePrefsStorage / SitePreferencesBar): consent update, config, SPA page_view.
 */

import { allowsPublicAnalyticsSync } from '@/utils/sitePrefsStorage'

const GA_SCRIPT = 'https://www.googletagmanager.com/gtag/js'

let scriptInjected = false
let configSent = false

/**
 * Normalize GA4 measurement ID from Vite env (build-time). Handles quotes, BOM, accidental URL paste.
 */
function getMeasurementId() {
  const raw = import.meta.env.VITE_GA4_MEASUREMENT_ID
  if (raw === undefined || raw === null) return ''
  let id = String(raw).replace(/^\uFEFF/, '').trim()
  if ((id.startsWith('"') && id.endsWith('"')) || (id.startsWith("'") && id.endsWith("'"))) {
    id = id.slice(1, -1).trim()
  }
  const extracted = id.match(/G-[A-Za-z0-9]+/)
  if (extracted) id = extracted[0]
  if (!/^G-[A-Za-z0-9]+$/.test(id)) {
    if (import.meta.env.DEV && String(raw).trim() !== '') {
      console.warn(
        '[ga4] VITE_GA4_MEASUREMENT_ID is set but not a valid GA4 ID. Use G-XXXXXXXX (letters/numbers only after G-). Raw:',
        raw,
      )
    }
    return ''
  }
  return id
}

export function isGa4Configured() {
  return !!getMeasurementId()
}

function ensureDataLayer() {
  if (typeof window === 'undefined') return
  window.dataLayer = window.dataLayer || []
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments)
    }
  }
}

/**
 * Call once at app startup (before Vue mount). Sets Consent Mode defaults so gtag never assumes granted.
 */
export function installGa4ConsentShell() {
  if (typeof window === 'undefined') return
  ensureDataLayer()
  if (!isGa4Configured()) return
  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  })
}

function injectGtagScript(measurementId) {
  return new Promise((resolve, reject) => {
    if (scriptInjected) {
      resolve()
      return
    }
    const existing = document.querySelector(`script[src^="${GA_SCRIPT}"]`)
    if (existing) {
      scriptInjected = true
      resolve()
      return
    }
    const s = document.createElement('script')
    s.async = true
    s.src = `${GA_SCRIPT}?id=${encodeURIComponent(measurementId)}`
    s.onload = () => {
      scriptInjected = true
      resolve()
    }
    s.onerror = () => reject(new Error('gtag script failed to load'))
    document.head.appendChild(s)
  })
}

/**
 * @param {boolean} analyticsAllowed - mirrors Pinia / allowsPublicAnalyticsSync()
 */
export async function syncGa4WithConsent(analyticsAllowed) {
  if (typeof window === 'undefined') return
  const measurementId = getMeasurementId()
  if (!measurementId) return

  ensureDataLayer()

  if (!analyticsAllowed) {
    if (typeof window.gtag === 'function' && configSent) {
      window.gtag('consent', 'update', { analytics_storage: 'denied' })
    }
    return
  }

  try {
    await injectGtagScript(measurementId)
    window.gtag('js', new Date())
    window.gtag('consent', 'update', { analytics_storage: 'granted' })
    window.gtag('config', measurementId, {
      send_page_view: false,
    })
    configSent = true
    trackGa4PageView()
  } catch (e) {
    if (import.meta.env.DEV) {
      console.warn('[ga4] gtag.js did not load (ad blocker, network, or CSP).', e)
    }
  }
}

/**
 * SPA virtual page views (landing, /app, /r/:slug, restaurant subdomains, etc.)
 */
export function trackGa4PageView() {
  if (typeof window === 'undefined') return
  if (!allowsPublicAnalyticsSync()) return
  const measurementId = getMeasurementId()
  if (!measurementId || !configSent || typeof window.gtag !== 'function') return

  const path = window.location.pathname + window.location.search
  window.gtag('config', measurementId, {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title || undefined,
  })
}

/**
 * @param {import('vue-router').Router} router
 */
export function initGa4RouterHooks(router) {
  router.afterEach(() => {
    trackGa4PageView()
  })
}
